'use client';

import type { ReactNode } from 'react';
import { openConfirmModal } from '@mantine/modals';
import { FiAlertCircle } from 'react-icons/fi';

import { Flex } from '../layout/flex';
import type { ModalProps } from '../modal';
import modalClasses from '../modal/modal.module.css';

/** The only things that differ between the confirm and the delete prompt. */
interface ConfirmPromptChrome {
  /** Leading icon in the title row, already resolved against the caller's `icon`. */
  icon: ReactNode;
  /** Title node used when the caller passed no `title`. */
  defaultTitle: ReactNode;
  /** Body used when the caller passed neither `confirmationMessage` nor `children`. */
  defaultBody?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  /** Theme color key for the confirm button — `red` for a destructive prompt. */
  confirmColor?: string;
  size?: string;
  variant?: ModalProps['variant'];
}

interface ConfirmPromptCall {
  confirmationMessage?: ReactNode;
  children?: ReactNode;
  title?: string;
  /**
   * `data-testid` for the confirm button, for the Playwright suites that click it by test id. Only
   * `useConfirmModal` passes it through — no delete prompt is driven by test id today.
   */
  confirmTestId?: string;
  onCancel?: () => void;
  onClose?: () => void;
  onConfirm: (() => void) | (() => Promise<void>);
}

/** The icon of a prompt that warns rather than destroys. */
export const WarningIcon = () => (
  <FiAlertCircle color={'var(--mantine-color-yellow-filled)'} size={24} />
);

/** The title row every core-ui prompt opens with. */
export const PromptTitle = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <Flex align="center" gap="sm">
    {icon}
    {children}
  </Flex>
);

/**
 * The same scrolling region core-ui's Modal renders, so a long prompt scrolls under the fixed
 * action row instead of pushing it off-screen.
 */
export const PromptBody = ({ children }: { children: ReactNode }) => (
  // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard-scrollable region
  <div className={modalClasses.scrollRegion} tabIndex={0}>
    {children}
  </div>
);

/**
 * Shared body of `useConfirmModal` and `useConfirmDeleteModal`. Both open one imperative Mantine
 * confirm modal and differed only by icon, labels, default copy and confirm color — everything else
 * (the title row, the dismissal semantics, the cancel button matching `ModalButtons`) lives here so
 * the two hooks can't drift apart.
 *
 * Padding, title weight and the overlay blur come from the theme's Modal entry.
 */
export const openConfirmPrompt = (chrome: ConfirmPromptChrome, call: ConfirmPromptCall) => {
  const { confirmationMessage, children, confirmTestId, title, onCancel, onClose, onConfirm } =
    call;

  openConfirmModal({
    title: <PromptTitle icon={chrome.icon}>{title ?? chrome.defaultTitle}</PromptTitle>,
    // Mantine wraps this in a Box of its own, which the modal.module.css rule flattens into a
    // pass-through.
    children: <PromptBody>{confirmationMessage ?? children ?? chrome.defaultBody}</PromptBody>,
    labels: { confirm: chrome.confirmLabel, cancel: chrome.cancelLabel },
    // Mantine's own cancel is already `variant="default"`, the same bordered button ModalButtons
    // uses, so the imperative prompt and the declarative ActionModal footer read as one control.
    confirmProps: {
      color: chrome.confirmColor,
      'data-testid': confirmTestId,
    },
    // Mantine renders this Group as the last child of the modal body, which is where the fixed
    // action row lives — so it takes the same class the declarative Modal gives its footer slot.
    groupProps: { className: modalClasses.footer },
    closeOnConfirm: true,
    closeOnCancel: true,
    size: chrome.size,
    variant: chrome.variant,
    onCancel,
    onClose,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onConfirm,
  });
};
